/**
 * Network Settings — expandable home for per-network options (v1: default log level).
 */
import React, { useState, useEffect } from 'react';
import { getRestApiBearerToken } from '@/services/apiToken';
import { motion } from 'framer-motion';
import {
  NETWORK_LOG_LEVELS,
  DEFAULT_NETWORK_LOG_LEVEL,
  normalizeLogLevel
} from '@/constants/networkLogLevel';
import {
  NETWORK_HUMAN_REVIEW_OPTIONS,
  DEFAULT_NETWORK_HUMAN_REVIEW_MODE,
  normalizeNetworkHumanReviewMode
} from '@/constants/networkHumanReviewMode';
import {
  VECTOR_DB_PROVIDERS,
  SELECTABLE_VECTOR_DB_PROVIDERS,
  DEFAULT_VECTOR_DB_PROVIDER,
  normalizeVectorDbProvider
} from '@/constants/vectorDbProvider';
import { canAccessWorkspaceAdmin } from '@/utils/stepflowRoles';
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.stepflow.dev';

export default function SettingsTab({ network, workspaceRole, onNetworkUpdate }) {
  const [level, setLevel] = useState(DEFAULT_NETWORK_LOG_LEVEL);
  const [humanReviewMode, setHumanReviewMode] = useState(DEFAULT_NETWORK_HUMAN_REVIEW_MODE);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);

  const networkId = network?.networkId;
  const savedLevel = normalizeLogLevel(network?.logLevel);
  const savedHumanReview = normalizeNetworkHumanReviewMode(network?.networkHumanReviewMode);
  const savedPoolTenantMetering =
    network?.runtimeConfig?.meteringPoolTenantEnabled === undefined
      ? false
      : !!network.runtimeConfig.meteringPoolTenantEnabled;

  const savedUsageMeteringHelp =
    network?.runtimeConfig?.usageMeteringHelpEnabled === undefined
      ? false
      : !!network.runtimeConfig.usageMeteringHelpEnabled;

  const savedVectorDb = normalizeVectorDbProvider(network?.vectorDbProvider);
  const vectorDbFrozen = !!network?.vectorDbFrozen;
  const canEditVectorDb = canAccessWorkspaceAdmin(workspaceRole);

  const [poolTenantMetering, setPoolTenantMetering] = useState(false);
  const [usageMeteringHelp, setUsageMeteringHelp] = useState(false);
  const [vectorDbProvider, setVectorDbProvider] = useState(DEFAULT_VECTOR_DB_PROVIDER);

  useEffect(() => {
    setLevel(savedLevel);
  }, [savedLevel, networkId]);

  useEffect(() => {
    setHumanReviewMode(savedHumanReview);
  }, [savedHumanReview, networkId]);

  useEffect(() => {
    setPoolTenantMetering(savedPoolTenantMetering);
  }, [savedPoolTenantMetering, networkId]);

  useEffect(() => {
    setUsageMeteringHelp(savedUsageMeteringHelp);
  }, [savedUsageMeteringHelp, networkId]);

  useEffect(() => {
    setVectorDbProvider(savedVectorDb);
  }, [savedVectorDb, networkId]);

  const dirty =
    level !== savedLevel ||
    humanReviewMode !== savedHumanReview ||
    poolTenantMetering !== savedPoolTenantMetering ||
    usageMeteringHelp !== savedUsageMeteringHelp ||
    (canEditVectorDb && !vectorDbFrozen && vectorDbProvider !== savedVectorDb);

  const handleSave = async () => {
    if (!networkId || networkId === 'new' || !dirty) return;
    setSaving(true);
    setSaveMessage(null);
    try {
      const idToken = getRestApiBearerToken();
      const response = await fetch(`${API_BASE_URL}/networks/${networkId}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          logLevel: level,
          networkHumanReviewMode: humanReviewMode,
          ...(canEditVectorDb && !vectorDbFrozen && vectorDbProvider !== savedVectorDb
            ? { vectorDbProvider }
            : {}),
          runtimeConfig: {
            ...(network?.runtimeConfig || {}),
            meteringPoolTenantEnabled: poolTenantMetering,
            usageMeteringHelpEnabled: usageMeteringHelp
          }
        })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.message || data.error || response.statusText);
      }
      const net = data.network;
      onNetworkUpdate?.({
        logLevel: level,
        networkHumanReviewMode: humanReviewMode,
        runtimeConfig: {
          ...(network?.runtimeConfig || {}),
          meteringPoolTenantEnabled: poolTenantMetering,
          usageMeteringHelpEnabled: usageMeteringHelp
        },
        ...(net && {
          vectorDbProvider: normalizeVectorDbProvider(net.vectorDbProvider),
          vectorDbFrozen: !!net.vectorDbFrozen,
          logLevel: net.logLevel,
          networkHumanReviewMode: net.networkHumanReviewMode,
          runtimeConfig: net.runtimeConfig
        })
      });
      setSaveMessage({ type: 'success', text: 'Settings saved.' });
    } catch (e) {
      setSaveMessage({ type: 'error', text: e.message || 'Save failed' });
    } finally {
      setSaving(false);
    }
  };

  if (!networkId || networkId === 'new') {
    return (
      <div className="h-full flex items-center justify-center bg-gray-50 p-6 dark:bg-slate-950/40">
        <p className="text-gray-600 text-sm max-w-md text-center dark:text-slate-300">
          Save the network from the Build tab to open Settings. New networks use{' '}
          <span className="font-medium text-gray-800 dark:text-slate-100">S3 Vectors</span> as the vector index by
          default—you can change it here before anything runs that locks the choice.
        </p>
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto bg-gray-50/80">
      <div className="max-w-2xl mx-auto p-8 space-y-8">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Network settings</h2>
          <p className="text-sm text-gray-600 mt-1">
            Stored on this network. Use the Save settings button at the bottom to persist changes.
          </p>
        </div>

        <motion.section
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 space-y-4"
        >
          <h3 className="text-sm font-semibold text-gray-800">Vector index</h3>
          <p className="text-xs text-gray-600 leading-relaxed">
            Choose which managed vector index backs search and memory for this network. Additional options may be
            added later. Once any workflow has run (build, run, or inbound processing), this choice is locked until
            migration is supported.
          </p>
          <label className="flex flex-col gap-1.5 max-w-md">
            <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">Index backend</span>
            <select
              value={vectorDbProvider}
              onChange={(e) => {
                setVectorDbProvider(normalizeVectorDbProvider(e.target.value));
                setSaveMessage(null);
              }}
              disabled={vectorDbFrozen || !canEditVectorDb}
              className="text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white text-gray-900 disabled:bg-gray-100 disabled:text-gray-500"
            >
              {/* v1: only S3 Vectors is selectable (USAGE-AND-BUDGET.md §10).
                  But if THIS network is already frozen on a suppressed provider
                  (legacy LanceDB/dual), still show its real value as a disabled
                  option so the UI stays truthful rather than mislabeling it. */}
              {(SELECTABLE_VECTOR_DB_PROVIDERS.some((o) => o.value === vectorDbProvider)
                ? SELECTABLE_VECTOR_DB_PROVIDERS
                : [
                    ...SELECTABLE_VECTOR_DB_PROVIDERS,
                    VECTOR_DB_PROVIDERS.find((o) => o.value === vectorDbProvider),
                  ].filter(Boolean)
              ).map((opt) => (
                <option
                  key={opt.value}
                  value={opt.value}
                  disabled={!SELECTABLE_VECTOR_DB_PROVIDERS.some((o) => o.value === opt.value)}
                >
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
          {!canEditVectorDb && (
            <p className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
              Only workspace owners and admins can change the vector index for this network.
            </p>
          )}
          {vectorDbFrozen && (
            <p className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
              This choice is locked because a workflow has already run for this network. Changing the index will
              require a future migration.
            </p>
          )}
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 space-y-4"
        >
          <h3 className="text-sm font-semibold text-gray-800">Logging</h3>
          <label className="flex flex-col gap-1.5 max-w-xs">
            <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">Default log level</span>
            <select
              value={level}
              onChange={(e) => {
                setLevel(e.target.value);
                setSaveMessage(null);
              }}
              className="text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white text-gray-900"
            >
              {NETWORK_LOG_LEVELS.map((v) => (
                <option key={v} value={v}>
                  {v}
                  {v === DEFAULT_NETWORK_LOG_LEVEL ? ' (system default)' : ''}
                </option>
              ))}
            </select>
            <span className="text-xs text-gray-500">
              Used when a trigger does not send <code className="text-gray-700">logLevel</code>. Run tab can still
              override per run.
            </span>
          </label>
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 space-y-4"
        >
          <h3 className="text-sm font-semibold text-gray-800">Run &amp; API (light) human review</h3>
          <p className="text-xs text-gray-600">
            Controls Step Functions runs started from the Run tab or execute API. Inbound email to agent addresses
            always follows each agent&apos;s permissions.
          </p>
          <div className="flex flex-col gap-3">
            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">When agents run via Run / API</span>
              <select
                value={humanReviewMode}
                onChange={(e) => {
                  setHumanReviewMode(e.target.value);
                  setSaveMessage(null);
                }}
                className="text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white text-gray-900 max-w-xl"
              >
                {NETWORK_HUMAN_REVIEW_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <span className="text-xs text-gray-500">
                {NETWORK_HUMAN_REVIEW_OPTIONS.find((o) => o.value === humanReviewMode)?.hint}
              </span>
            </label>
          </div>
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.08 }}
          className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 space-y-4"
        >
          <h3 className="text-sm font-semibold text-gray-800">Customer pool</h3>
          <p className="text-xs text-gray-600 leading-relaxed">
            Optional attribution: extra LLM metering rows keyed by <strong className="font-medium text-gray-700">customer pool</strong>{' '}
            (Dynamo SK includes <code className="text-gray-800">#POOL#</code>) so you can roll up usage by slug in
            Usage → <strong className="font-medium text-gray-700">By customer pool</strong>. Only applies when the Run
            payload includes a non-empty <code className="text-gray-800">tenantId</code> or{' '}
            <code className="text-gray-800">customerId</code> (e.g. <code className="text-gray-800">acme-corp</code>). Workspace
            IDs (<code className="text-gray-800">TN-…</code>) are not used as the pool slug. Leave off if you only need
            workspace-level Bedrock totals.
          </p>
          <label className="flex items-start gap-3 cursor-pointer max-w-xl">
            <input
              type="checkbox"
              className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              checked={poolTenantMetering}
              onChange={(e) => {
                setPoolTenantMetering(e.target.checked);
                setSaveMessage(null);
              }}
            />
            <span className="text-sm text-gray-800 leading-snug">
              Enable customer pool LLM attribution
            </span>
          </label>
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 space-y-4"
        >
          <h3 className="text-sm font-semibold text-gray-800">Usage tab</h3>
          <p className="text-xs text-gray-600 leading-relaxed">
            When off (default), the network <strong className="font-medium text-gray-700">Usage</strong> →{' '}
            <strong className="font-medium text-gray-700">LLM tokens</strong> view stays compact. Turn on to show
            workspace vs customer pool explanations, the API metering note, and related helper copy.
          </p>
          <label className="flex items-start gap-3 cursor-pointer max-w-xl">
            <input
              type="checkbox"
              className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              checked={usageMeteringHelp}
              onChange={(e) => {
                setUsageMeteringHelp(e.target.checked);
                setSaveMessage(null);
              }}
            />
            <span className="text-sm text-gray-800 leading-snug">Show metering details on Usage</span>
          </label>
        </motion.section>

        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !dirty}
            className="px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed w-fit"
          >
            {saving ? 'Saving…' : 'Save settings'}
          </button>
          {saveMessage && (
            <p
              className={`text-sm ${saveMessage.type === 'success' ? 'text-green-700' : 'text-red-600'}`}
              role="status"
            >
              {saveMessage.text}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
